import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import CSS from 'csstype';
import { debounce } from 'lodash-es';
import React, { useMemo, useState } from 'react';

import { BracketIcon } from '~/src/dash/components/icons/misc-icons';
import { borderRadius, Div, fontSizing } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

type InputSize = 'extraSmall' | 'small';

type ISizeStyles = Record<InputSize, ISizeStyle>;

interface ISizeStyle {
  inputStyle: CSS.Properties;
  iconWrapperStyle: CSS.Properties;
}

const sizeStyles: ISizeStyles = {
  extraSmall: {
    inputStyle: { fontSize: fontSizing.extraSmall },
    iconWrapperStyle: { top: '6px' },
  },
  small: {
    inputStyle: { fontSize: fontSizing.small },
    iconWrapperStyle: { top: '7px' },
  },
};

type TextInputPrefix = 'search' | 'search_alt';

interface ITextInputProps {
  enableClear?: boolean; // displays clear button on right
  fullWidth?: boolean;
  handleOnChange: (text: string) => void;
  initialValue?: string;
  isDisabled?: boolean;
  inputSize?: InputSize;
  inputStyle?: CSS.Properties;
  maxLength?: number;
  minLength?: number;
  msDelay?: number; // add delay to debounce. default 0. recommended 300 or 500 if needing debounce
  placeholder?: string;
  prefix?: TextInputPrefix; // displays icon/symbol on left
  width?: CSS.WidthProperty<string | 0> | undefined;
  isAutoFocused?: boolean;
  type?: string;
  min?: number;
  step?: number;
  inputID?: string;
}

export const TextInput = ({
  enableClear,
  fullWidth,
  handleOnChange,
  isAutoFocused,
  isDisabled = false,
  initialValue,
  inputSize = 'extraSmall',
  inputStyle = {},
  maxLength = 524288, // true input default
  minLength = 0,
  msDelay = 0,
  placeholder,
  prefix,
  width = '240px',
  type = 'text',
  min,
  step,
  inputID,
}: ITextInputProps) => {
  const [input, setInput] = useState(initialValue || '');

  const debouncedOnChange = useMemo(() => {
    return debounce(handleOnChange, msDelay);
  }, [handleOnChange]);

  const onChange = (value: string) => {
    setInput(value);
    debouncedOnChange(value);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const clipboardData = e.clipboardData?.getData('Text') || '';
    onChange(clipboardData);
  };

  const sizeStyle = sizeStyles[inputSize];

  return (
    <Div style={{ width: fullWidth ? '100%' : width }}>
      <Div style={{ width: '100%' }}>
        <input
          autoFocus={isAutoFocused}
          className='text-input-base'
          type={type}
          min={min}
          step={step}
          placeholder={placeholder}
          value={input}
          onChange={(e: React.FormEvent<HTMLInputElement>) => {
            e.stopPropagation();
            e.preventDefault();
            onChange(e.currentTarget.value);
          }}
          onPaste={onPaste}
          maxLength={maxLength}
          minLength={minLength}
          style={{
            background: isDisabled ? publicaColors.veryLightGray : publicaColors.white,
            border: `0.5px solid ${publicaColors.lightGray}`,
            borderRadius: borderRadius.small,
            boxShadow: isDisabled ? 'none' : 'inset 1px 2px 8px rgba(0, 0, 0, 0.05)',
            fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
            paddingTop: '6px',
            paddingRight: enableClear ? '30px' : '12px',
            paddingBottom: '6px',
            paddingLeft: prefix ? '30px' : '12px',
            width: '100%',
            ...sizeStyle.inputStyle,
            ...inputStyle,
          }}
          id={inputID || 'text-input-base'}
        />
      </Div>
      {prefix && (
        <Div
          style={{
            position: 'absolute',
            left: '6px',
            height: '16px',
            ...sizeStyle.iconWrapperStyle,
          }}
        >
          {prefix === 'search' && (
            <SearchIcon
              style={{
                color: publicaColors.gray,
                height: '16px',
              }}
            />
          )}
          {prefix === 'search_alt' && (
            <BracketIcon
              color={publicaColors.gray}
              style={{
                height: '16px',
                marginLeft: '6px',
                paddingBottom: '1px',
              }}
            />
          )}
        </Div>
      )}
      {enableClear && input !== '' && (
        <Div
          onClick={() => onChange('')}
          style={{
            position: 'absolute',
            right: '6px',
            height: '16px',
            ...sizeStyle.iconWrapperStyle,
          }}
        >
          <ClearIcon
            style={{
              color: publicaColors.gray,
              height: '16px',
            }}
          />
        </Div>
      )}
    </Div>
  );
};
